import { zodResolver } from "@hookform/resolvers/zod";
import { BarChart3, ExternalLink } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { CreateTrafficSourceInput, UpdateTrafficSourceInput } from "@/gql/graphql";
import { useIsMobile } from "@/hooks/hooks/use-mobile";
import { useTrafficSources } from "../hooks/use-traffic-sources";

// Schema will be created inside the component to access translations

interface TrafficSourceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trafficSource?: any; // TrafficSource from GraphQL
}

export function TrafficSourceDialog({
  open,
  onOpenChange,
  trafficSource,
}: TrafficSourceDialogProps) {
  const { t } = useTranslation("settings");
  const { createTrafficSource, updateTrafficSource, isCreating, isUpdating } = useTrafficSources();
  const isMobile = useIsMobile();
  const isEditing = !!trafficSource;
  const isLoading = isCreating || isUpdating;

  const trafficSourceSchema = z.object({
    code: z
      .string()
      .min(1, t("trafficSources.validation.codeRequired"))
      .max(50, t("trafficSources.validation.codeMaxLength"))
      .regex(/^[a-zA-Z0-9-_]+$/, t("trafficSources.validation.codeFormat")),
    name: z
      .string()
      .min(1, t("trafficSources.validation.nameRequired"))
      .max(100, t("trafficSources.validation.nameMaxLength")),
    description: z
      .string()
      .max(500, t("trafficSources.validation.descriptionMaxLength"))
      .optional(),
    isActive: z.boolean(),
  });

  type TrafficSourceFormData = z.infer<typeof trafficSourceSchema>;

  const form = useForm<TrafficSourceFormData>({
    resolver: zodResolver(trafficSourceSchema),
    defaultValues: {
      code: "",
      name: "",
      description: "",
      isActive: true,
    },
  });

  // Reset form when dialog opens/closes or trafficSource changes
  useEffect(() => {
    if (open) {
      if (trafficSource) {
        form.reset({
          code: trafficSource.code,
          name: trafficSource.name,
          description: trafficSource.description || "",
          isActive: trafficSource.isActive,
        });
      } else {
        form.reset({
          code: "",
          name: "",
          description: "",
          isActive: true,
        });
      }
    }
  }, [open, trafficSource, form]);

  const onSubmit = async (data: TrafficSourceFormData) => {
    try {
      if (isEditing) {
        const updateInput: UpdateTrafficSourceInput = {
          code: data.code,
          name: data.name,
          description: data.description || undefined,
          isActive: data.isActive,
        };
        await updateTrafficSource(trafficSource.id, updateInput);
      } else {
        const createInput: CreateTrafficSourceInput = {
          code: data.code,
          name: data.name,
          description: data.description || undefined,
          isActive: data.isActive,
        };
        await createTrafficSource(createInput);
      }
      onOpenChange(false);
    } catch (_error) {
      // Error handling is done in the hook
    }
  };

  const generateExampleUrl = () => {
    const code = form.watch("code");
    if (!code) return "";
    return `https://yoursalon.com/book?source=${code}`;
  };

  const formContent = (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="code"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("trafficSources.code")}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t("trafficSources.codePlaceholder")}
                    {...field}
                    disabled={isLoading}
                  />
                </FormControl>
                <FormDescription>{t("trafficSources.validation.codeFormat")}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("trafficSources.name")}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t("trafficSources.namePlaceholder")}
                    {...field}
                    disabled={isLoading}
                  />
                </FormControl>
                <FormDescription>{t("trafficSources.appointmentCountDescription")}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Optional description for internal reference"
                    {...field}
                    disabled={isLoading}
                    rows={3}
                  />
                </FormControl>
                <FormDescription>
                  Optional description to help your team understand this traffic source
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="isActive"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">Active</FormLabel>
                  <FormDescription>
                    Active traffic sources are available for new appointments
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={isLoading}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        {/* URL Example */}
        {form.watch("code") && (
          <>
            <Separator />
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <ExternalLink className="h-4 w-4" />
                <span className="text-sm font-medium">Example URL</span>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <code className="text-sm">{generateExampleUrl()}</code>
              </div>
              <p className="text-xs text-muted-foreground">
                Customers who visit this URL will have appointments automatically tagged with this
                traffic source.
              </p>
            </div>
          </>
        )}

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 mt-6 mb-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading
              ? isEditing
                ? "Updating..."
                : "Creating..."
              : isEditing
                ? "Update Traffic Source"
                : "Create Traffic Source"}
          </Button>
        </div>
      </form>
    </Form>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="h-[90vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              {isEditing
                ? t("trafficSources.editTrafficSource")
                : t("trafficSources.createTrafficSource")}
            </SheetTitle>
            <SheetDescription>{t("trafficSources.description")}</SheetDescription>
          </SheetHeader>
          <div className="mt-6">{formContent}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            {isEditing
              ? t("trafficSources.editTrafficSource")
              : t("trafficSources.createTrafficSource")}
          </DialogTitle>
          <DialogDescription>{t("trafficSources.description")}</DialogDescription>
        </DialogHeader>
        {formContent}
      </DialogContent>
    </Dialog>
  );
}
